import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { GetPaymentsDocument, type GetPaymentsQuery } from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import {
  paymentStateColors,
  translatePaymentDirection,
  translatePaymentState,
} from '@/utils/billingStateLabels';

interface PaymentRow {
  id: string;
  customerName: string;
  state: string;
  direction: string;
  amountNet: number;
  amountGross: number;
  currencyCode?: string | null;
  reference?: string | null;
  paymentDate?: string | null;
  createdAt: string;
  accountingDocument?: { number: string; tenantId?: string } | null;
  paymentGateway?: { name: string } | null;
  tenantId?: string;
  [key: string]: unknown;
}

export default function Payments() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatCurrency = (amount?: number | null, currency?: string | null) =>
    amount != null
      ? new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'EUR' }).format(
          amount,
        )
      : '–';
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';

  const { data, loading } = useQuery<GetPaymentsQuery>(GetPaymentsDocument, {
    variables: { first: 50 },
  });

  const payments = data?.payments?.nodes ?? [];
  const totalCount = data?.payments?.totalCount ?? 0;

  const rows: PaymentRow[] = payments.map((p) => ({
    id: p.id,
    customerName:
      p.accountingDocument?.subscription?.organizationUnit?.organization?.name ??
      p.accountingDocument?.tenant?.name ??
      '–',
    state: p.state,
    direction: p.direction,
    amountNet: p.amountNet,
    amountGross: p.amountGross,
    currencyCode: p.currencyCode,
    reference: p.reference,
    paymentDate: p.paymentDate,
    createdAt: p.createdAt,
    accountingDocument: p.accountingDocument,
    paymentGateway: p.paymentGateway,
    tenantId: (p.accountingDocument as { tenantId?: string } | undefined)?.tenantId,
  }));

  const columns: GridColDef[] = [
    {
      field: 'customerName',
      headerName: t('customers.customerOrg'),
      width: 200,
      valueGetter: (_v, row) => row.customerName,
    },
    {
      field: 'accountingDocument',
      headerName: t('common.invoice'),
      width: 140,
      valueGetter: (value: unknown) => (value as { number?: string })?.number ?? '–',
    },
    {
      field: 'state',
      headerName: t('common.status'),
      width: 110,
      renderCell: (params) => (
        <Chip
          label={translatePaymentState(t, String(params.value))}
          size="small"
          color={paymentStateColors[String(params.value)] || 'default'}
        />
      ),
    },
    {
      field: 'direction',
      headerName: t('common.direction'),
      width: 120,
      valueFormatter: (value: string) => translatePaymentDirection(t, value),
    },
    {
      field: 'amountGross',
      headerName: t('common.amount'),
      width: 110,
      valueFormatter: (value: number) => formatCurrency(value),
    },
    {
      field: 'paymentGateway',
      headerName: t('common.gateway'),
      width: 130,
      valueGetter: (value: unknown) => (value as { name?: string })?.name ?? '–',
    },
    {
      field: 'paymentDate',
      headerName: t('common.paymentDate'),
      width: 130,
      valueFormatter: (value: string) => formatDate(value),
    },
    { field: 'reference', headerName: t('common.reference'), flex: 1 },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 110,
      valueFormatter: (value: string) => formatDate(value),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 180px)', width: '100%', minWidth: 0 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t('payments.title')}
      </Typography>
      <DataTable<PaymentRow>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onRowClick={(row) => navigate(`/payments/${row.id}`)}
        paginationMode="client"
      />
    </Box>
  );
}
